import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';

const TermsConditionsPage = () => {
  return (
    <>
      <Helmet>
        <title>Terms & Conditions - Grow Guardian</title>
        <meta name="description" content="Terms and Conditions for Grow Guardian - Review our service terms, user responsibilities, and legal agreements" />
      </Helmet>
      
      <div className="min-h-screen bg-gray-50">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-lg shadow-sm p-6 sm:p-8 lg:p-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Terms & Conditions
            </h1>
            <p className="text-sm text-gray-600 mb-8">
              Effective Date: December 26, 2025
            </p>

            <div className="prose prose-lg max-w-none space-y-8">
              {/* Introduction */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  1. Introduction
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Welcome to Grow Guardian! These Terms and Conditions ("Terms") govern your access 
                  to and use of the Grow Guardian application and website (collectively, the "Service") 
                  operated by Grow Guardian LLC ("we," "us," or "our"). By creating an account or using 
                  our Service, you agree to be bound by these Terms. If you do not agree to these Terms, 
                  please do not use our Service.
                </p>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Contact Information:</strong> For questions about these Terms, contact us at 
                  <a href="mailto:admin@growguardian.work" className="text-green-600 hover:text-green-700 ml-1">admin@growguardian.work</a>
                </p>
              </section>

              {/* Service Description */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  2. Service Description
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Grow Guardian is a comprehensive cultivation and Integrated Pest Management (IPM) 
                  task management platform designed for agricultural professionals. Our Service provides:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Task scheduling and management for cultivation operations</li>
                  <li>IPM planning, tracking, and compliance documentation</li>
                  <li>Treatment plan creation and analytics</li>
                  <li>Team collaboration and performance monitoring</li>
                  <li>AI-powered assistance for pest detection and deficiency identification</li>
                  <li>Chemical usage tracking and regulatory compliance tools</li>
                  <li>Learning resources for IPM best practices</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  We reserve the right to modify, suspend, or discontinue any feature of the Service 
                  at any time with or without notice.
                </p>
              </section>

              {/* SMS Opt-In Requirements - NEW SECTION */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  3. SMS Opt-In Requirements and Consent
                </h2>
                <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded mb-4">
                  <p className="text-gray-800 leading-relaxed font-semibold mb-2">
                    IMPORTANT: SMS Communication Consent
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Grow Guardian offers optional SMS notifications to enhance your experience. 
                    SMS consent is NOT required to view or use the app. SMS features are only 
                    activated if you explicitly opt in.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">
                  Required Checkboxes on Phone Number Collection
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  When you provide your phone number in our app or website, you will encounter 
                  two separate checkboxes:
                </p>

                <div className="bg-white border-2 border-gray-300 rounded-lg p-5 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-start">
                    <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2 flex-shrink-0">1</span>
                    SMS Consent Checkbox (Unchecked by Default)
                  </h4>
                  <div className="ml-8 bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-gray-800 leading-relaxed italic">
                      "I agree to receive SMS messages from Grow Guardian LLC about account notifications, 
                      reminders, and IPM/TPM updates. Msg & data rates may apply. Msg frequency varies. 
                      Reply STOP to cancel, HELP for help. Privacy Policy: 
                      <a href="https://growguardian.work/privacy" className="text-green-600 hover:text-green-700 ml-1">
                        https://growguardian.work/privacy
                      </a>"
                    </p>
                  </div>
                  <ul className="list-disc pl-6 mt-3 space-y-1 text-gray-700 text-sm">
                    <li><strong>Not pre-checked:</strong> You must actively check this box to opt in</li>
                    <li><strong>Optional:</strong> Not required to use the app or view content</li>
                    <li><strong>Separate consent:</strong> Independent from Terms of Service agreement</li>
                    <li><strong>Clear language:</strong> Includes message purpose, frequency, rates, and opt-out instructions</li>
                  </ul>
                </div>

                <div className="bg-white border-2 border-gray-300 rounded-lg p-5 mb-4">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-start">
                    <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2 flex-shrink-0">2</span>
                    Terms of Service Checkbox (Separate)
                  </h4>
                  <div className="ml-8 bg-gray-50 p-4 rounded border border-gray-200">
                    <p className="text-gray-800 leading-relaxed italic">
                      "I agree to the Terms of Service: 
                      <a href="https://growguardian.work/terms" className="text-green-600 hover:text-green-700 ml-1">
                        https://growguardian.work/terms
                      </a>"
                    </p>
                  </div>
                  <ul className="list-disc pl-6 mt-3 space-y-1 text-gray-700 text-sm">
                    <li><strong>Separate checkbox:</strong> Distinct from SMS consent</li>
                    <li><strong>General terms:</strong> Covers overall service usage, not SMS specifically</li>
                    <li><strong>Required for account:</strong> Must be accepted to create an account</li>
                  </ul>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">
                  Opt-In Workflow
                </h3>
                <div className="bg-green-50 border border-green-300 rounded-lg p-5">
                  <p className="text-gray-800 leading-relaxed mb-3">
                    <strong>How Users Consent to SMS Communications:</strong>
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Users opt in via an unchecked SMS consent checkbox on our signup/phone capture screen. 
                    The SMS consent checkbox is separate from our Terms of Service agreement. The consent 
                    language includes message purpose, msg frequency varies, msg & data rates may apply, 
                    and STOP/HELP instructions with a link to our Privacy Policy. Users can opt out any 
                    time by replying STOP.
                  </p>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">
                  Key Compliance Points
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>No Pre-Checked Boxes:</strong> SMS consent checkbox is never pre-checked; users must actively opt in</li>
                  <li><strong>Not Required for App Access:</strong> SMS consent is optional and only needed for SMS notification features</li>
                  <li><strong>Separate from Terms:</strong> SMS consent is not buried in Terms of Service or Privacy Policy</li>
                  <li><strong>Clear Opt-Out:</strong> Users can reply STOP at any time to unsubscribe from all SMS messages</li>
                  <li><strong>Transparent Disclosure:</strong> All consent language includes frequency, rates, purpose, and opt-out instructions</li>
                  <li><strong>Privacy Policy Link:</strong> Direct link to privacy policy provided in consent language</li>
                </ul>

                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>SMS Service Provider:</strong> Our SMS messages are delivered via Twilio. 
                  By opting in to SMS notifications, you acknowledge that your phone number will be 
                  shared with Twilio solely for the purpose of delivering messages on our behalf.
                </p>
              </section>

              {/* Account Responsibility - RENUMBERED TO 4 */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  4. Account Registration and Responsibilities
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Account Creation:</strong> You must provide accurate and complete information 
                  when creating an account. You are responsible for maintaining the confidentiality of 
                  your account credentials and for all activities that occur under your account.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Eligibility:</strong> You must be at least 18 years old to create an account 
                  and use our Service. By creating an account, you represent and warrant that you meet 
                  this age requirement.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Account Security:</strong> You agree to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Use a strong, unique password for your account</li>
                  <li>Notify us immediately of any unauthorized access or security breach</li>
                  <li>Not share your account credentials with others</li>
                  <li>Not create multiple accounts for fraudulent purposes</li>
                  <li>Log out from shared devices to prevent unauthorized access</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  You are solely responsible for any activity conducted through your account, whether 
                  authorized or unauthorized.
                </p>
              </section>

              {/* Acceptable Use */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  5. Acceptable Use Policy
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  You agree to use the Service only for lawful purposes and in accordance with these Terms. 
                  You agree NOT to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Violate any applicable local, state, national, or international law</li>
                  <li>Infringe upon the intellectual property rights of others</li>
                  <li>Upload or transmit viruses, malware, or other malicious code</li>
                  <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                  <li>Use the Service to harass, abuse, or harm others</li>
                  <li>Engage in any data scraping, mining, or automated data collection</li>
                  <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
                  <li>Use the Service for any commercial purpose without our written consent</li>
                  <li>Impersonate any person or entity or misrepresent your affiliation</li>
                  <li>Interfere with or disrupt the Service or servers/networks connected to it</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  Violation of this Acceptable Use Policy may result in immediate termination of your 
                  account and legal action.
                </p>
              </section>

              {/* SMS Terms */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  6. SMS Communication Terms
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>SMS Usage:</strong> You agree to receive SMS notifications from Grow Guardian 
                  LLC for account-related updates, reminders, and IPM/TPM information. You can manage 
                  your SMS preferences in your account settings.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Opt-Out:</strong> You can opt out of receiving SMS notifications at any time 
                  by replying "STOP" to the last SMS message you receive from us.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Message Content:</strong> SMS messages may include account-related information, 
                  reminders, and updates on IPM/TPM tasks. The content is sent using our best efforts 
                  to ensure accuracy.
                </p>
              </section>

              {/* Intellectual Property */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  7. Intellectual Property Rights
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Ownership:</strong> All content, features, and functionality of the Service, 
                  including but not limited to text, graphics, logos, icons, images, audio clips, data 
                  compilations, and software, are the exclusive property of Grow Guardian LLC or its 
                  licensors and are protected by copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>License:</strong> Subject to your compliance with these Terms, we grant you a 
                  limited, non-exclusive, non-transferable, revocable license to access and use the 
                  Service for your personal or internal business purposes.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>User Content:</strong> You retain ownership of any data, content, or information 
                  you submit to the Service ("User Content"). By submitting User Content, you grant us a 
                  worldwide, non-exclusive, royalty-free license to use, store, and process your User 
                  Content solely to provide and improve the Service.
                </p>
              </section>

              {/* Subscription and Payment */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  8. Subscription and Payment Terms
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Free and Premium Tiers:</strong> Grow Guardian offers both free and premium 
                  subscription plans with varying features and capabilities.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Premium Subscriptions:</strong> Premium subscriptions are billed on a recurring 
                  basis (monthly or annually) and will automatically renew unless cancelled before the 
                  renewal date.
                </p>

                {/* Auto-Renewing Subscription Details */}
                <div className="bg-green-50 border border-green-300 rounded-lg p-5 mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Auto-Renewing Subscription Plans
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    The following auto-renewing subscription options are currently available:
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-green-300 rounded text-sm">
                      <thead>
                        <tr className="bg-green-100">
                          <th className="border border-green-300 px-4 py-3 text-left font-semibold text-gray-900">Subscription Title</th>
                          <th className="border border-green-300 px-4 py-3 text-left font-semibold text-gray-900">Length</th>
                          <th className="border border-green-300 px-4 py-3 text-left font-semibold text-gray-900">Price</th>
                          <th className="border border-green-300 px-4 py-3 text-left font-semibold text-gray-900">Price Per Unit</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="bg-white">
                          <td className="border border-green-300 px-4 py-3 text-gray-800 font-medium">Grow Guardian Pro – Monthly</td>
                          <td className="border border-green-300 px-4 py-3 text-gray-700">1 Month</td>
                          <td className="border border-green-300 px-4 py-3 text-gray-700">$14.99 / month</td>
                          <td className="border border-green-300 px-4 py-3 text-gray-700">$14.99 per month</td>
                        </tr>
                        <tr className="bg-green-50">
                          <td className="border border-green-300 px-4 py-3 text-gray-800 font-medium">Grow Guardian Pro – Annual</td>
                          <td className="border border-green-300 px-4 py-3 text-gray-700">12 Months</td>
                          <td className="border border-green-300 px-4 py-3 text-gray-700">$79.99 / year</td>
                          <td className="border border-green-300 px-4 py-3 text-gray-700">~$6.67 per month</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-4">
                  <p className="text-gray-800 leading-relaxed">
                    <strong>Auto-Renewal Notice:</strong> Subscriptions automatically renew unless auto-renew is turned off at least 
                    24 hours before the end of the current subscription period. Your account will be charged for renewal within 
                    24 hours prior to the end of the current period. You can manage and cancel your subscription in your 
                    App Store account settings after purchase. No cancellation of the current subscription is allowed during the active subscription period.
                  </p>
                </div>

                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Payment Processing:</strong> Payments are processed securely through third-party 
                  payment processors. We do not store your complete payment card information.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Cancellation:</strong> You may cancel your premium subscription at any time 
                  through your App Store account settings. Cancellations take effect at the end of the current 
                  billing period. We do not provide refunds for partial subscription periods.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Price Changes:</strong> We reserve the right to modify subscription prices with 
                  30 days' advance notice. Price changes will apply to subsequent billing periods.
                </p>

                {/* Apple EULA */}
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-5">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    End User License Agreement (EULA)
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Use of Grow Guardian is subject to Apple's Standard Licensed Application End User License Agreement (EULA). 
                    In the event of any conflict between these Terms and the Apple EULA, the Apple EULA shall govern with respect 
                    to App Store purchases. You can review the Apple EULA at:
                  </p>
                  <a 
                    href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-green-600 hover:text-green-700 underline break-all"
                  >
                    https://www.apple.com/legal/internet-services/itunes/dev/stdeula/
                  </a>
                </div>
              </section>

              {/* Disclaimer and Limitation of Liability */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  9. Disclaimer of Warranties and Limitation of Liability
                </h2>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mb-4">
                  <p className="text-gray-800 leading-relaxed font-semibold mb-2">
                    IMPORTANT LEGAL NOTICE - PLEASE READ CAREFULLY
                  </p>
                  <p className="text-gray-800 leading-relaxed">
                    THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
                    EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO 
                    IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND 
                    NON-INFRINGEMENT.
                  </p>
                </div>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>No Agricultural or Pest Management Advice:</strong> Grow Guardian is a task 
                  management and documentation tool. We do not provide professional agricultural advice, 
                  pest management recommendations, or regulatory compliance guidance. Always consult with 
                  licensed professionals and follow applicable laws and regulations for your jurisdiction.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>AI Features:</strong> Our AI-powered features (pest detection, deficiency 
                  identification, chat assistant) are provided as assistive tools and may not always be 
                  accurate. We are not responsible for decisions made based on AI-generated suggestions. 
                  Always verify information with qualified professionals.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Limitation of Liability:</strong> TO THE MAXIMUM EXTENT PERMITTED BY LAW, 
                  GROW GUARDIAN LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
                  CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, DATA, OR USE, 
                  ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF WE HAVE BEEN ADVISED 
                  OF THE POSSIBILITY OF SUCH DAMAGES.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT YOU 
                  PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100 USD, WHICHEVER 
                  IS GREATER.
                </p>
              </section>

              {/* Indemnification */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  10. Indemnification
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Grow Guardian LLC, its officers, 
                  directors, employees, agents, and affiliates from and against any claims, liabilities, 
                  damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out 
                  of or relating to: (a) your use of the Service; (b) your violation of these Terms; 
                  (c) your violation of any rights of another party; or (d) your User Content.
                </p>
              </section>

              {/* Termination */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  11. Termination
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Termination by You:</strong> You may terminate your account at any time by 
                  contacting us at admin@growguardian.work or through your account settings. Upon 
                  termination, your access to the Service will cease immediately.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Termination by Us:</strong> We reserve the right to suspend or terminate your 
                  account and access to the Service at any time, with or without notice, for any reason, 
                  including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Violation of these Terms or our Acceptable Use Policy</li>
                  <li>Fraudulent, abusive, or illegal activity</li>
                  <li>Extended periods of inactivity</li>
                  <li>Non-payment of subscription fees</li>
                  <li>At our sole discretion for any other reason</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-3">
                  <strong>Effect of Termination:</strong> Upon termination, your right to use the Service 
                  will immediately cease. We may delete your User Content after a reasonable period. 
                  Provisions of these Terms that by their nature should survive termination will survive, 
                  including but not limited to intellectual property rights, disclaimers, indemnification, 
                  and limitations of liability.
                </p>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  12. Governing Law and Dispute Resolution
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Governing Law:</strong> These Terms shall be governed by and construed in 
                  accordance with the laws of the State of [Your State], United States, without regard 
                  to its conflict of law provisions.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Dispute Resolution:</strong> Any disputes arising out of or relating to these 
                  Terms or the Service shall first be attempted to be resolved through good faith 
                  negotiation between you and Grow Guardian LLC.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Arbitration:</strong> If negotiations fail, disputes shall be resolved through 
                  binding arbitration in accordance with the rules of the American Arbitration Association. 
                  The arbitration shall take place in [Your State], and judgment on the award may be 
                  entered in any court having jurisdiction.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Class Action Waiver:</strong> You agree that any dispute resolution proceedings 
                  will be conducted only on an individual basis and not in a class, consolidated, or 
                  representative action.
                </p>
              </section>

              {/* Modifications to Terms */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  13. Changes to These Terms
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify these Terms at any time. When we make material changes, 
                  we will notify you by email or through a prominent notice in the Service at least 30 
                  days before the changes take effect. Your continued use of the Service after the 
                  effective date of the revised Terms constitutes your acceptance of the changes. If you 
                  do not agree to the modified Terms, you must stop using the Service and terminate your 
                  account.
                </p>
              </section>

              {/* Miscellaneous */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  14. Miscellaneous
                </h2>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Entire Agreement:</strong> These Terms, together with our Privacy Policy, 
                  constitute the entire agreement between you and Grow Guardian LLC regarding the Service 
                  and supersede all prior agreements.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Severability:</strong> If any provision of these Terms is found to be 
                  unenforceable, the remaining provisions will remain in full force and effect.
                </p>
                <p className="text-gray-700 leading-relaxed mb-3">
                  <strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms 
                  will not be deemed a waiver of such right or provision.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Assignment:</strong> You may not assign or transfer these Terms or your account 
                  without our prior written consent. We may assign these Terms without restriction.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                  15. Contact Information
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  If you have any questions, concerns, or feedback regarding these Terms, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p className="text-gray-800">
                    <strong>Grow Guardian LLC</strong><br />
                    Email: <a href="mailto:admin@growguardian.work" className="text-green-600 hover:text-green-700">admin@growguardian.work</a><br />
                    Website: <a href="https://growguardian.work" className="text-green-600 hover:text-green-700">https://growguardian.work</a>
                  </p>
                </div>
              </section>

              {/* Acknowledgment */}
              <section className="border-t-2 border-gray-200 pt-6">
                <p className="text-gray-700 leading-relaxed font-semibold">
                  BY CREATING AN ACCOUNT OR USING THE GROW GUARDIAN SERVICE, YOU ACKNOWLEDGE THAT YOU 
                  HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS.
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsConditionsPage;